import { useState } from 'react'
import { useAuth } from 'ict24h-themes-react'
import {
  SearchQueryProjectInput,
  ProjectEntity,
  Paging,
} from '../../../../../server-api-hrm.type'
import commonRequestHandler from '../../../../../utils/common-request-handler'
import { MetadataType } from '../../../../../utils/metadata.types'
import requestErrorHandler from '../../../../../utils/request-error-handler'
import uniqueArrayById from '../../../../../utils/unique-array-by-id'
import {
  ProjectListVariant,
  getListProject,
  getDetailProject,
} from '../project.service'
import updateOneItemInList from '../../../../../utils/update-one-item-in-list'
import { setProjectDetail } from '../stores/project-reducer'

type LoadType = 'fetch' | 'load-more' | 're-fetch'
type RequestFailType = 'SERVER' | 'RESOURCE' | 'OTHER'

type Params = {
  listVariant?: ProjectListVariant
  filter?: SearchQueryProjectInput | null
  onGetListFinish?: (_data: ProjectEntity[]) => void
  onGetListFail?: (_failType: RequestFailType) => void
  onGetDetailFail?: (_failType: RequestFailType) => void
  onGetDetailFinish?: (_data: ProjectEntity) => void
  autoGetFullData?: boolean
  customRequestHandler?: boolean
}

type GetDataParams = {
  type: LoadType
  paging?: Paging
  filter?: SearchQueryProjectInput
  localHandler?: Omit<Params, 'onGetDetailFail' | 'onGetDetailFinish'>
}

type GetDetailParams = {
  id?: string
  refetchInList?: boolean
  localHandler?: Pick<
    Params,
    'customRequestHandler' | 'onGetDetailFail' | 'onGetDetailFinish'
  >
}

const useGetDataProject = (params: Params = {}) => {
  const { token } = useAuth()

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ProjectEntity[]>([])
  const [detail, setDetail] = useState<ProjectEntity>()
  const [metadata, setMetadata] = useState<MetadataType>()

  const handleResetData = () => {
    setData([])
    setMetadata(undefined)
  }

  const getData = async ({
    type,
    filter: localFilter,
    paging,
    localHandler = {},
  }: GetDataParams) => {
    const filter = { ...params.filter, ...localFilter }
    const listVariant = localHandler.listVariant || params.listVariant
    const onFinish = localHandler.onGetListFinish || params.onGetListFinish
    const onFail = localHandler.onGetListFail || params.onGetListFail
    const autoGetFullData =
      localHandler.autoGetFullData ?? params.autoGetFullData
    const customRequestHandler =
      localHandler?.customRequestHandler ?? params.customRequestHandler

    const requestHandler = customRequestHandler
      ? requestErrorHandler
      : commonRequestHandler

    if (type === 'fetch') handleResetData()
    setLoading(true)
    const res = await getListProject(token, listVariant, {
      paging: { pageLimit: autoGetFullData ? 100 : undefined, ...paging },
      filter,
    })
    setLoading(false)

    if (!res) {
      if (typeof onFail === 'function') onFail('OTHER')
      return
    }

    // Lợi dụng setState để lấy data vừa update mới nhất
    const finishFlow = () =>
      setData((prev) => {
        if (typeof onFinish === 'function') onFinish(prev)
        // return lại data gốc
        return prev
      })

    requestHandler({
      status: res?.status,
      onSuccess: () => {
        setMetadata(res.data?.metadata)
        setData((prev) =>
          type === 'load-more'
            ? uniqueArrayById([...prev, ...(res.data?.data || [])])
            : res.data?.data || []
        )

        if (autoGetFullData) {
          const nextPageId = res?.data?.metadata?.paging?.cursor?.next
          if (nextPageId) {
            getData({
              type: 'load-more',
              paging: { pageLimit: 100, pageId: nextPageId },
              filter,
              localHandler,
            })
          } else {
            finishFlow()
          }
        } else {
          finishFlow()
        }
      },
      onResourceFail: () => {
        if (typeof onFail === 'function') onFail('RESOURCE')
      },
      onServerFail: () => {
        if (typeof onFail === 'function') onFail('SERVER')
      },
    })
  }

  const getDetail = async ({
    id,
    refetchInList,
    localHandler = {},
  }: GetDetailParams) => {
    if (!id) return
    const onFinish = localHandler.onGetDetailFinish || params.onGetDetailFinish
    const onFail = localHandler.onGetDetailFail || params.onGetDetailFail
    const customRequestHandler =
      localHandler?.customRequestHandler ?? params.customRequestHandler

    const requestHandler = customRequestHandler
      ? requestErrorHandler
      : commonRequestHandler

    setLoading(true)
    const res = await getDetailProject(token, { id })
    setLoading(false)

    if (!res || !res.data) {
      if (typeof onFail === 'function') onFail('OTHER')
      return
    }
    requestHandler({
      status: res?.status,
      onSuccess: () => {
        setDetail(res.data)
        setProjectDetail(res.data)
        if (refetchInList) {
          setData(
            (prev) =>
              updateOneItemInList(res.data, prev) ||
              uniqueArrayById([...prev, res.data])
          )
        }
        if (typeof onFinish === 'function') onFinish(res.data)
      },
      onResourceFail: () => {
        if (typeof onFail === 'function') onFail('RESOURCE')
      },
      onServerFail: () => {
        if (typeof onFail === 'function') onFail('SERVER')
      },
    })
  }

  return {
    loading,
    data,
    detail,
    metadata,
    getDetail,
    getData,
  }
}

export default useGetDataProject
